import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { Plus, Trash2, Globe, Send, ChevronDown, Copy, Eye, EyeOff, KeyRound, RefreshCw } from 'lucide-react';
import { settingsApi } from '../../../api/endpoints/settings';
import { Input } from '../../../components/ui/Input';
import { Button } from '../../../components/ui/Button';
import { Toggle } from '../../../components/ui/Toggle';
import { EmptyState } from '../../../components/ui/EmptyState';
import { Modal } from '../../../components/ui/Modal';
import { useToast } from '../../../components/ui/Alert';
import type { StoreSettings } from '../../../types/settings';

interface WebhookEntry {
  url: string;
  events: string[];
  isActive: boolean;
}

interface Props {
  settings?: StoreSettings;
  storeId: string;
}

// Events the backend actually emits via sendStoreEvent. Keep in sync with
// 00pay/code/routes/stores/store-app/sendStoreEvent.js call sites.
const AVAILABLE_EVENTS = [
  'order.created',
  'order.updated',
  'order.paid',
  'order.cancelled',
  'cart.created',
  'cart.abandoned',
  'cart.coupon.applied',
  'customer.created',
  'customer.loggedin',
  'product.created',
  'product.updated',
  'review.store.added',
  'review.product.added',
];

export function WebhooksTab({ settings, storeId }: Props) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const eventLabel = (event: string): string => {
    // i18next uses '.' as nesting separator, so encode the dotted event name.
    const key = `settingsWebhooks.eventLabels.${event.replace(/\./g, '_')}`;
    const translated = t(key);
    return translated === key ? event : translated;
  };

  const rawWebhooks = (settings as any)?.webhooks;
  const webhooks: WebhookEntry[] = (Array.isArray(rawWebhooks) ? rawWebhooks : []).map((w: any) => ({
    url: typeof w?.url === 'string' ? w.url : '',
    events: Array.isArray(w?.events) ? w.events : [],
    isActive: w?.isActive !== false,
  }));
  const [entries, setEntries] = useState<WebhookEntry[]>(webhooks);
  const [testMenuOpen, setTestMenuOpen] = useState<number | null>(null);
  const [testingIndex, setTestingIndex] = useState<number | null>(null);

  // The backend mints the secret on the first save that configures a webhook,
  // so it is tracked locally rather than read from `settings` on every render.
  const [secret, setSecret] = useState<string | null>(settings?.webhookSecret ?? null);
  const [secretRevealed, setSecretRevealed] = useState(false);
  const [rotateOpen, setRotateOpen] = useState(false);

  const { register, handleSubmit, reset } = useForm({
    defaultValues: { url: '' },
  });

  const mutation = useMutation({
    mutationFn: (data: { webhooks: WebhookEntry[] }) => settingsApi.updateWebhooks(storeId, data),
    onSuccess: (result) => {
      if (result?.webhookSecret) setSecret(result.webhookSecret);
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t('settingsWebhooks.saveSuccess'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const secretMutation = useMutation({
    mutationFn: () => settingsApi.rotateWebhookSecret(storeId),
    onSuccess: (newSecret) => {
      const wasRotation = !!secret;
      setSecret(newSecret);
      setSecretRevealed(true);
      setRotateOpen(false);
      queryClient.invalidateQueries({ queryKey: ['settings', storeId] });
      toast('success', t(wasRotation ? 'settingsWebhooks.signing.rotated' : 'settingsWebhooks.signing.generated'));
    },
    onError: () => toast('error', t('common.error')),
  });

  const copySecret = () => {
    if (!secret) return;
    navigator.clipboard.writeText(secret).catch(() => {});
    toast('success', t('common.copied', 'Copied'));
  };

  const addWebhook = (data: { url: string }) => {
    if (!data.url.trim()) return;
    setEntries([...entries, { url: data.url.trim(), events: AVAILABLE_EVENTS.slice(), isActive: true }]);
    reset({ url: '' });
  };

  const removeWebhook = (index: number) => {
    setEntries(entries.filter((_, i) => i !== index));
  };

  const toggleEvent = (index: number, event: string) => {
    setEntries(entries.map((e, i) => {
      if (i !== index) return e;
      const events = e.events.includes(event)
        ? e.events.filter(ev => ev !== event)
        : [...e.events, event];
      return { ...e, events };
    }));
  };

  const toggleActive = (index: number) => {
    setEntries(entries.map((e, i) => i === index ? { ...e, isActive: !e.isActive } : e));
  };

  const sendTest = async (index: number, event: string) => {
    const entry = entries[index];
    if (!entry?.url) return;
    setTestMenuOpen(null);
    setTestingIndex(index);
    try {
      const result = await settingsApi.testWebhook(storeId, { url: entry.url, event });
      if (result.delivered) {
        toast('success', t('settingsWebhooks.testSuccess', {
          status: result.status,
          ms: result.elapsedMs,
        }));
      } else {
        toast('error', t('settingsWebhooks.testNon2xx', {
          status: result.status,
          statusText: result.statusText || '',
        }));
      }
    } catch (err: any) {
      const msg = err?.response?.data?.error || err?.message || t('common.error');
      toast('error', msg);
    } finally {
      setTestingIndex(null);
    }
  };

  return (
    <div className="space-y-6">
      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <h2 className="text-base font-semibold">{t('settingsWebhooks.title')}</h2>

        {entries.length === 0 ? (
          <EmptyState title={t('settingsWebhooks.empty')} />
        ) : (
          <div className="space-y-4">
            {entries.map((entry, i) => (
              <div key={i} className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-3">
                <div className="flex items-center gap-3">
                  <Globe className="h-4 w-4 text-text-muted shrink-0" />
                  <code className="flex-1 text-sm text-accent truncate">{entry.url}</code>

                  <div className="relative">
                    <button
                      type="button"
                      disabled={testingIndex === i || entry.events.length === 0}
                      onClick={() => setTestMenuOpen(testMenuOpen === i ? null : i)}
                      className="flex items-center gap-1 rounded-lg border border-border-default px-2.5 py-1 text-xs font-medium text-text-muted hover:bg-bg-hover hover:text-text disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      <Send className="h-3.5 w-3.5" />
                      {testingIndex === i ? t('settingsWebhooks.sending') : t('settingsWebhooks.sendTest')}
                      <ChevronDown className="h-3 w-3" />
                    </button>
                    {testMenuOpen === i && (
                      <div className="absolute end-0 top-full z-20 mt-1 max-h-72 w-56 overflow-y-auto rounded-lg border border-border-default bg-bg-primary py-1 shadow-lg">
                        {entry.events.length === 0 ? (
                          <div className="px-3 py-2 text-xs text-text-muted">
                            {t('settingsWebhooks.noEventsSelected')}
                          </div>
                        ) : (
                          entry.events.map(event => (
                            <button
                              key={event}
                              type="button"
                              onClick={() => sendTest(i, event)}
                              className="block w-full px-3 py-1.5 text-start text-xs hover:bg-bg-hover"
                            >
                              {eventLabel(event)}
                              <span className="ms-2 text-text-muted">{event}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </div>

                  <Toggle checked={entry.isActive} onChange={() => toggleActive(i)} />
                  <button
                    onClick={() => removeWebhook(i)}
                    className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-danger"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {AVAILABLE_EVENTS.map(event => (
                    <button
                      key={event}
                      type="button"
                      onClick={() => toggleEvent(i, event)}
                      className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                        entry.events.includes(event)
                          ? 'border-accent bg-accent/10 text-accent'
                          : 'border-border-default text-text-muted hover:border-text-muted'
                      }`}
                    >
                      {eventLabel(event)}
                    </button>
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}

        <form onSubmit={handleSubmit(addWebhook)} className="flex items-end gap-3">
          <Input
            id="webhook-url"
            label={t('settingsWebhooks.url')}
            placeholder="https://example.com/webhook"
            {...register('url')}
            className="flex-1"
          />
          <Button type="submit" variant="secondary" size="sm">
            <Plus className="h-4 w-4" />
            {t('settingsWebhooks.addWebhook')}
          </Button>
        </form>
      </section>

      <section className="rounded-xl border border-border-default bg-bg-secondary p-6 space-y-4">
        <div className="flex items-start gap-3">
          <KeyRound className="mt-0.5 h-4 w-4 shrink-0 text-text-muted" />
          <div className="space-y-1">
            <h2 className="text-base font-semibold">{t('settingsWebhooks.signing.title')}</h2>
            <p className="text-sm text-text-muted">{t('settingsWebhooks.signing.description')}</p>
          </div>
        </div>

        {secret ? (
          <>
            <div className="flex items-center gap-2 rounded-lg border border-border-default bg-bg-tertiary p-3">
              <code className="flex-1 truncate font-mono text-sm" dir="ltr">
                {secretRevealed ? secret : `${secret.slice(0, 12)}${'•'.repeat(24)}`}
              </code>
              <button
                type="button"
                onClick={() => setSecretRevealed(!secretRevealed)}
                title={t(secretRevealed ? 'settingsWebhooks.signing.hide' : 'settingsWebhooks.signing.reveal')}
                className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
              >
                {secretRevealed ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
              <button
                type="button"
                onClick={copySecret}
                title={t('common.copy', 'Copy')}
                className="rounded-lg p-1.5 text-text-muted hover:bg-bg-hover hover:text-accent"
              >
                <Copy className="h-4 w-4" />
              </button>
            </div>
            <Button variant="secondary" size="sm" onClick={() => setRotateOpen(true)}>
              <RefreshCw className="h-4 w-4" />
              {t('settingsWebhooks.signing.rotate')}
            </Button>
          </>
        ) : (
          <Button
            variant="secondary"
            size="sm"
            onClick={() => secretMutation.mutate()}
            isLoading={secretMutation.isPending}
          >
            <KeyRound className="h-4 w-4" />
            {t('settingsWebhooks.signing.generate')}
          </Button>
        )}

        <div className="rounded-lg border border-border-default bg-bg-tertiary p-4 space-y-2">
          <p className="text-xs font-medium">{t('settingsWebhooks.signing.howToTitle')}</p>
          <p className="text-xs text-text-muted">{t('settingsWebhooks.signing.howToBody')}</p>
          <pre className="overflow-x-auto rounded-md bg-bg-primary p-3 text-xs" dir="ltr">
            <code>{`const expected = 'sha256=' + crypto
  .createHmac('sha256', ZERO_WEBHOOK_SECRET)
  .update(rawRequestBody)
  .digest('hex');

expected === req.headers['x-zero-signature'];`}</code>
          </pre>
          <p className="text-xs text-text-muted">{t('settingsWebhooks.signing.rawBodyNote')}</p>
        </div>
      </section>

      <div className="flex justify-end">
        <Button
          onClick={() => mutation.mutate({ webhooks: entries })}
          isLoading={mutation.isPending}
        >
          {t('common.save')}
        </Button>
      </div>

      <Modal
        isOpen={rotateOpen}
        onClose={() => setRotateOpen(false)}
        title={t('settingsWebhooks.signing.rotate')}
        footer={
          <>
            <Button variant="secondary" onClick={() => setRotateOpen(false)}>
              {t('common.cancel')}
            </Button>
            <Button
              variant="danger"
              onClick={() => secretMutation.mutate()}
              isLoading={secretMutation.isPending}
            >
              {t('settingsWebhooks.signing.rotate')}
            </Button>
          </>
        }
      >
        <p className="text-sm text-text-muted">{t('settingsWebhooks.signing.rotateWarning')}</p>
      </Modal>
    </div>
  );
}
